import { useState, useMemo } from 'react';
import { ChevronRight, ChevronDown, Link2 } from 'lucide-react';

const LEVEL_COLORS = {
  1: '#6366f1', 2: '#3b82f6', 3: '#14b8a6', 4: '#f59e0b', 5: '#f43f5e',
};

const STATUS_COLORS = {
  Active: 'bg-emerald-100 text-emerald-700',
  Draft: 'bg-slate-100 text-slate-700',
  Retired: 'bg-red-100 text-red-600',
};

const TYPE_COLORS = {
  Core: 'bg-blue-50 text-blue-700',
  Support: 'bg-purple-50 text-purple-700',
  Management: 'bg-amber-50 text-amber-700',
};

// Build a flat ordered list with depth info for rendering
function flattenTree(processes) {
  const childrenMap = {};
  const roots = [];
  for (const p of processes) {
    childrenMap[p.id] = [];
  }
  for (const p of processes) {
    if (p.parent_process_id && childrenMap[p.parent_process_id] !== undefined) {
      childrenMap[p.parent_process_id].push(p);
    } else {
      roots.push(p);
    }
  }
  const result = [];
  const stack = roots.map(r => ({ proc: r, depth: 0 })).reverse();
  while (stack.length > 0) {
    const item = stack.pop();
    const kids = childrenMap[item.proc.id] || [];
    result.push({ ...item, hasChildren: kids.length > 0 });
    for (let i = kids.length - 1; i >= 0; i--) {
      stack.push({ proc: kids[i], depth: item.depth + 1 });
    }
  }
  return result;
}

function ProcessTree({ processes, selectedId, onSelect }) {
  const [expandedIds, setExpandedIds] = useState(() => {
    const ids = new Set();
    if (processes) processes.forEach(p => { if (p.level <= 2) ids.add(p.id); });
    return ids;
  });

  const flatList = useMemo(() => flattenTree(processes || []), [processes]);

  const handleToggle = (id, e) => {
    e.stopPropagation();
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Filter visible nodes: only show if all ancestors are expanded
  const visibleNodes = useMemo(() => {
    const parentMap = {};
    for (const p of (processes || [])) {
      parentMap[p.id] = p.parent_process_id;
    }
    return flatList.filter(item => {
      let pid = item.proc.parent_process_id;
      while (pid) {
        if (!expandedIds.has(pid)) return false;
        pid = parentMap[pid];
      }
      return true;
    });
  }, [flatList, expandedIds, processes]);

  return (
    <div data-testid="process-tree">
      <div className="flex items-center justify-between px-2 pb-2 border-b mb-2">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Hierarchy</span>
        <div className="flex gap-2">
          <button onClick={() => setExpandedIds(new Set((processes || []).map(p => p.id)))} className="text-xs text-slate-400 hover:text-slate-600">Expand</button>
          <button onClick={() => setExpandedIds(new Set())} className="text-xs text-slate-400 hover:text-slate-600">Collapse</button>
        </div>
      </div>
      {visibleNodes.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-8">No processes yet</p>
      )}
      {visibleNodes.map(item => {
        const p = item.proc;
        const isSelected = selectedId === p.id;
        const isExpanded = expandedIds.has(p.id);
        const linkCount = p.linked_counts ? p.linked_counts.total : 0;
        return (
          <div
            key={p.id}
            style={{ paddingLeft: item.depth * 20 + 8, background: isSelected ? 'rgba(35,88,148,0.1)' : undefined }}
            className="flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer hover:bg-slate-50 text-sm"
            onClick={() => onSelect(p.id)}
            data-testid={'tree-node-' + p.id}
          >
            <button className="w-4 h-4 flex items-center justify-center flex-shrink-0" onClick={(e) => item.hasChildren && handleToggle(p.id, e)}>
              {item.hasChildren ? (isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />) : <span className="w-3.5" />}
            </button>
            <span className="w-5 h-5 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: LEVEL_COLORS[p.level] || '#94a3b8' }}>
              {p.level}
            </span>
            <span className={'truncate flex-1 ' + (isSelected ? 'font-semibold text-[#235894]' : 'text-slate-700')}>
              {p.name}
            </span>
            {linkCount > 0 && (
              <span className="flex items-center gap-0.5 text-xs text-slate-400 flex-shrink-0">
                <Link2 className="w-3 h-3" />{linkCount}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export { ProcessTree, LEVEL_COLORS, STATUS_COLORS, TYPE_COLORS };
